import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import { VERSION } from '@/constants';
import LoginService from '@/account/login.service';
import AccountService from '@/account/account.service';
import TranslationService from '@/locale/translation.service';
import { Gravatar } from '@/shared/gravatar/gravatar';
import { IUserCompany } from '@/shared/model/user-company.model';
import { IUserDetails } from '@/shared/model/user.model';
import { IUserShop } from '@/shared/model/user-shop.model';
import { Role } from '@/shared/model/enumerations/role.model';
// @ts-ignore: No custon type available for SVG

enum IActiveEntityType {
  SHOP = 'SHOP',
  COMPANY = 'COMPANY',
  NULL = 'NULL',
}

@Component({
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },
  },
})
export default class SlmNavbar extends Vue {
  @Inject('loginService')
  private loginService: () => LoginService;

  @Inject('translationService') private translationService: () => TranslationService;
  private currentLanguage = this.$store.getters.currentLanguage;
  private languages: any = this.$store.getters.languages;

  @Inject('accountService') private accountService: () => AccountService;
  public version = VERSION ? 'v' + VERSION : '';
  private hasAnyAuthorityValue = false;
  private isAdmin: boolean = false;
  private gravatarImgUrl = '';
  public key = 1;
  public activeShop = -1;
  public activeCompany = -1;
  public activeEntity: any = {};
  public isAuthenticated = false;
  public userShops: IUserShop[] = [];
  public userCompanies: IUserCompany[] = [];
  public doLogout = true;
  public dialog = false;

  created() {
    this.translationService().refreshTranslation(this.currentLanguage);
  }

  public mounted(): void {
    if (this.$route.meta?.authorities) {
      this.doLogout = true;
    } else {
      this.doLogout = false;
    }
    this.initialize();
    this.setUserEntities();
  }

  public subIsActive(input) {
    const paths = Array.isArray(input) ? input : [input];
    return paths.some(path => {
      return this.$route.path.indexOf(path) === 0; // current path starts with this path string
    });
  }

  public changeLanguage(newLanguage: string): void {
    this.translationService().refreshTranslation(newLanguage);
  }

  public isActiveLanguage(key: string): boolean {
    return key === this.$store.getters.currentLanguage;
  }

  public logout(): void {
    this.clearStates();
    this.$forceUpdate();
    this.accountService().logout();
    this.dialog = false;
  }

  public clearStates(): void {
    this.activeShop = -1;
    this.activeCompany = -1;
    this.isAuthenticated = false;
    this.userShops = [];
    this.userCompanies = [];
    this.activeEntity = {};
    this.isAdmin = false;
  }

  public openLogin(): void {
    this.loginService().openLogin((<any>this).$root);
  }

  public get authenticated(): boolean {
    return this.$store.getters.authenticated;
  }

  public toggleDrawer(): void {
    return this.$store.commit('toggleDrawer');
  }
  public toggleRightDrawer(): void {
    return this.$store.commit('toggleRightDrawer');
  }

  public hasAnyAuthority(authorities: any): boolean {
    if (this.accountService().userAuthorities?.length) {
      this.hasAnyAuthorityValue = this.accountService().hasAnyAuthority(authorities);
    } else {
      this.accountService()
        .hasAnyAuthorityAndCheckAuth(authorities)
        .then(value => {
          this.hasAnyAuthorityValue = value;
        });
    }

    return this.hasAnyAuthorityValue;
  }

  @Watch('hasAnyAuthorityValue')
  @Watch('userCompanies')
  @Watch('isAdmin')
  public updateMenu() {
    this.key += 1;
  }

  public hasCompanyRole(companyRole: string): boolean {
    if (this.userCompanies.length && !this.isAdmin) {
      const activeShop = this.userShops.find(shop => shop.shop.id === this.$store.getters.activeEntity?.id);
      const activeCompanyId = activeShop.shop.company.id;
      return this.userCompanies.some(
        company => company.role === companyRole && (!activeCompanyId || company.company.id === activeCompanyId)
      );
    }
    return companyRole === Role.MANAGER;
  }

  public hasShopRole(userRole: string): boolean {
    if (this.userShops.length && !this.isAdmin) {
      const activeShopId = this.$store.getters.activeEntity?.id;
      return this.userShops.some(userShop => userShop.role === userRole && (!activeShopId || userShop.shop.id === activeShopId));
    }
    return this.isAdmin;
  }

  @Watch('authenticated')
  public setUserEntities() {
    if (!this.authenticated) return;

    const userRoles = ['ROLE_ADMIN'];
    if (!this.hasAnyAuthority(userRoles)) {
      if (this.$store.getters.activeEntity?.id) {
        const { index, type } = this.$store.getters.activeEntity;
        this.activeShop = type.toUpperCase() === 'SHOP' ? index : -1;
        this.activeCompany = type.toUpperCase() === 'COMPANY' ? index : -1;
        this.activeEntity = this.$store.getters.activeEntity || {};
      } else {
        if (this.$store.getters.shops.length > 0) {
          this.setActiveEntity(this.$store.getters.shops[0], IActiveEntityType.SHOP, 0);
        }
      }
      this.userShops = this.$store.getters.shops || [];
      this.userCompanies = this.$store.getters.companies || [];
    } else {
      this.isAdmin = true;
    }
  }

  public initialize(): void {
    const authorities = 'ROLE_USER';
    this.accountService().retrieveAccount(this.doLogout);
    this.accountService()
      .hasAnyAuthorityAndCheckAuth(authorities)
      .then(value => {
        this.hasAnyAuthorityValue = value;
      });
  }
  @Watch('$route', { immediate: true, deep: true })
  public setActiveEntity(entity: any, type: IActiveEntityType, index: number): void {
    if (typeof type !== 'string') return;
    const { id } = entity[type.toLowerCase()];
    const activeEntity = {
      index,
      id,
      name: entity.shop.name,
      role: entity.role,
      type,
      company: entity.shop.company,
    };

    this.activeEntity = activeEntity;
    this.$store.commit('setActiveEntity', activeEntity);
    (this.$root as Vue).$emit('active-shop');
    const allPaths = this.$router.options.routes.map(route => route.path);

    if (type === 'COMPANY') {
      this.activeCompany = index;
      this.activeShop = -1;
    } else {
      this.activeShop = index;
      this.activeCompany = -1;

      (this.$root as Vue).$emit('retrieve-header-notification'); // will retrieve header notification
      (this.$root as Vue).$emit('retrieve-notification'); // will retrieve notification page

      if (this.hasCompanyRole(Role.EMPLOYEE) && this.hasShopRole(Role.MANAGER)) {
        if (this.$route.fullPath.includes('company') || this.$route.fullPath.includes('shop')) {
          this.$router.replace('/shop');
        } else if (this.$route.fullPath.includes('user-access')) {
          this.$router.replace('/user-shop');
        }
      } else if (this.hasCompanyRole(Role.EMPLOYEE) && this.hasShopRole(Role.EMPLOYEE)) {
        if (allPaths.some(path => this.$route.fullPath.includes(path))) {
          if (this.$route.fullPath !== '/') {
            this.$router.replace('/');
          }
        }
      } else {
        if (this.$route.fullPath.includes('user-shop')) {
          this.$router.replace('/user-access');
        } else if (this.$route.fullPath.includes('shop')) {
          this.$router.replace('/company');
        }
      }
    }
  }

  public get user(): IUserDetails | any {
    if (this.$store.getters.account) {
      const { id, firstName, lastName, email, login } = this.$store.getters.account;
      const fullName = firstName + ' ' + lastName;
      const initials = this.userInitials(fullName);
      this.gravatarImgUrl = Gravatar.fetchImage(email);
      return {
        id,
        email,
        login,
        initials,
        fullName,
      };
    }
    return {};
  }

  userInitials(fullName: string): string {
    const fullNameArr = fullName.split(' ');
    const initials = fullNameArr.shift().charAt(0) + fullNameArr.pop().charAt(0);
    return initials.toUpperCase();
  }

  public get rightDrawer(): boolean {
    return this.$store.getters.rightDrawer;
  }
  public set rightDrawer(isOpen) {
    this.$store.commit('setRightDrawer', isOpen);
  }
}
